'use client';
import { useState, useEffect, useCallback } from 'react';
// import { v4 as uuidv4, NIL as emptyUuid } from 'uuid';
import {
	Navbar as NextUINavbar,
	NavbarContent,
	NavbarMenu,	
	NavbarBrand,
	NavbarItem,
} from "@nextui-org/navbar";
import { Link } from "@nextui-org/link";
// import { getServerSession } from "next-auth/next"
import { useSession } from 'next-auth/react';

import { Button, Popover, PopoverTrigger, PopoverContent, Divider } from "@nextui-org/react";
import { Kbd } from "@nextui-org/kbd";
import { Input } from "@nextui-org/input";

import NextLink from "next/link";



import {	
	DarIcon,
	SearchIcon,

} from "@/app/[lang]/components/icons";

import LocaleSwitcher from './locale-switcher';
import { UserAvatar } from "./user-avatar";
import { ThemeSwitch } from './theme-switch';
import SignOutButton from './auth/SignOutButton';
import { MenuWithItems, NavItemWithTranslates } from '@/types/modelTypes';
// import { title } from './primitives';
import flagEU from './../../../public/files/images/flags/european-union-50.png';
import flagUA from './../../../public/files/images/flags/ukraine-50.png';
import { siteConfig } from "@/config/site";

export const Navbar = ({ dictionary, lang, topMenu, leftMenu }: { dictionary: any, lang: string, topMenu?: MenuWithItems, leftMenu?: MenuWithItems }) => {

	const [isOpen, setIsOpen] = useState(false);
	const [mobileMenuOpened, setMobileMenuOpened] = useState<string[]>([]);
	
	// Expand only root level items by default
	const getAllVisibleIds = (items: any[]) => {
		const ids: string[] = [];
		items.forEach(item => {
			if (!item.hide) {
				ids.push(item.id);
			}
		});
		return ids;
	};

	const [opened, setOpened] = useState<string[]>(() => {
		if (leftMenu && leftMenu.navitems) {
			return getAllVisibleIds(leftMenu.navitems.filter(item => item.parentid === null));
		}
		return [];
	});

	useEffect(() => {
		if (leftMenu && leftMenu.navitems) {
			setOpened(getAllVisibleIds(leftMenu.navitems.filter(item => item.parentid === null)));
		}
	}, [leftMenu]);

	const [searchQuery, setSearchQuery] = useState('');
	
	const searchInput = (
		<Input
			aria-label="Search"
			classNames={{
				inputWrapper: "bg-default-100",
				input: "text-sm",
			}}
			endContent={
				<Kbd className="hidden lg:inline-block text-xs" keys={["command"]}>
					K
				</Kbd>
			}
			labelPlacement="outside"
			placeholder={dictionary?.search?.searchPlaceholder || "Search..."}
			size="sm"
			startContent={
				<SearchIcon className="text-base text-default-400 pointer-events-none flex-shrink-0" />
			}
			type="search"
			value={searchQuery}
			onChange={(e) => setSearchQuery(e.target.value)}
			onKeyDown={(e) => {
				if (e.key === 'Enter' && searchQuery.trim()) {
					window.location.href = `/${lang}/search?q=${encodeURIComponent(searchQuery.trim())}`;
				}
			}}
		/>
	);


	// const session = await getServerSession(authOptions);
	const { data: session } = useSession();

	// if(session && session.user)
	// 	console.log('session.user', session.user);

	const handleClickNode = useCallback((id: string) => {
		const index = opened.findIndex(i => i === id);
		if (index === -1) {
			opened.push(id);
		} else {
			opened.splice(index, 1);
		}
		setOpened([...opened]);
	}, [opened]);

	const handleMobileMenuToggle = useCallback((id: string) => {
		const index = mobileMenuOpened.findIndex(i => i === id);
		if (index === -1) {
			setMobileMenuOpened([...mobileMenuOpened, id]);
		} else {
			setMobileMenuOpened(mobileMenuOpened.filter(i => i !== id));
		}
	}, [mobileMenuOpened]);

	const isMobileMenuOpened = useCallback((id: string) => {
		return mobileMenuOpened.includes(id);
	}, [mobileMenuOpened]);

	/**
	 * @description Find opened node
	 * @param {string} id 
	 */
	const findOpened = useCallback((id: string) => {
		return opened.findIndex(i => i === id)
	}, [opened]);

	/**
	 * @description Build recursive tree 
	 * @param {NavItemWithTranslates} item
	 */
	const buildTreeNodeNavBar = useCallback((item: NavItemWithTranslates, level: number) => {
		const children = leftMenu?.navitems.filter(r => r.parentid === item.id && r.hide !== true);
		return (
			<li key={item.id} className='pl-2' >
				{children && children.length > 0 &&
					<>
						<button
							type="button"
							className="flex items-center p-2 w-full text-sm font-normal text-gray-900 rounded-lg transition duration-75 group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
							onClick={() => handleClickNode(item.id)}
						>
							<span className="material-symbols-outlined text-default-500">{item.icon}</span>
							<span className="flex-1 ml-3 text-left whitespace-nowrap">{item.translates.length > 0 ? item.translates[0].title : item.title}</span>
							<svg aria-hidden="true" className={"w-6 h-6 " + (findOpened(item.id) !== -1 ? "rotate-90" : "")} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
								<path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
							</svg>
						</button>

						{findOpened(item.id) !== -1 &&
							<ul id={item.id} className="py-1 space-y-2" >
								{children.map((child) => (
									buildTreeNodeNavBar(child, level + 1)
								))}
							</ul>
						}
					</>
				}

				{(!children || children.length === 0) &&
					<NextLink className="flex items-center p-2 text-sm font-normal text-gray-900 rounded-lg transition duration-75 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white group"
						href={`/${lang}${item.href}`}
						title={item.title}
					>
						<span className="material-symbols-outlined text-default-500">{item.icon}</span>
						<span className="ml-3">{item.translates.length > 0 ? item.translates[0].title : item.title}</span>
					</NextLink>
				}
			</li>
		);
	}, [findOpened, handleClickNode, lang, leftMenu?.navitems]);

	/**
	 * @description Build left tree navbar	 
	 */
	const buildLeftNavbar = useCallback(() => {
		const rootItems = leftMenu?.navitems.filter(r => r.parentid === null);
		return (
			<ul className="space-y-2">
				{/* {rootItems?.map((item: NavItemWithTranslates) => ( */}
				{rootItems?.filter(item => !item.hide).map((item: NavItemWithTranslates) => (
					buildTreeNodeNavBar(item as NavItemWithTranslates, 0)
				))}
			</ul>);
	}, [buildTreeNodeNavBar, leftMenu?.navitems]);

	// eslint-disable-next-line no-unused-vars
	const buildTopTreeNodeNavBar = useCallback((item: NavItemWithTranslates, level: number) => {

		if (!item || item.hide) {
			return null;
		}
		if (!topMenu || !topMenu.navitems) {
			return null;
		}
		if (!item.href) {
			return null;
		}

		const children = topMenu?.navitems.filter(r => r.parentid === item.id && r.hide !== true)
			.sort((a: NavItemWithTranslates, b: NavItemWithTranslates) => (a.stack) - (b.stack));

		if (!children || children.length === 0) {
			return (

				<NavbarItem key={item.id} className="hidden md:flex">

					<Button
						variant='light'
						disableRipple
						className="p-0 bg-transparent data-[hover=true]:bg-transparent text-sm"
						as={Link}
						href={item.href.startsWith('http') ? item.href : `/${lang}${item.href}`}																
						target={item.href.startsWith('http') ? "_blank" : "_self"}
					>
						{item.translates.length > 0 ? item.translates[0].title : item.title}
					</Button>
				</NavbarItem>
			)
		}

		if (children && children.length > 0) {
			return (
				<NavbarItem key={item.id} className="hidden md:flex">
					<Popover placement="bottom" showArrow={false} className='mt-1' >
						<PopoverTrigger>
							<Button
								variant='light'
								disableRipple
								className="p-0 bg-transparent data-[hover=true]:bg-transparent text-sm"
								radius="sm"
								endContent={
									<span className="material-symbols-outlined text-default-500 text-sm">arrow_drop_down</span>
								}
							>
								{item.translates.length > 0 ? item.translates[0].title : item.title}
							</Button>

						</PopoverTrigger>
						<PopoverContent >
							<div className="mx-auto grid gap-x-4 gap-y-6 px-4 py-6 max-w-sm sm:max-w-md lg:max-w-lg">
								<div className="col-span-6 gap-x-4 sm:gap-x-6">
									<div>
										<div className='pb-4'>
											<h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
												{item.translates.length > 0 ? item.translates[0].title : item.title}
											</h3>
											<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
												{item.descr}
											</div>
											<Divider className='mt-3' />
										</div>

										<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
											{children.map((child) => (
												<div key={child.id}>
													<div className="group relative flex gap-x-3 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700">
														<div>
															<a
																href={child.href.startsWith('http') ? child.href : `/${lang}${child.href}`}
																className="font-medium text-gray-900 dark:text-gray-300 text-sm block"
																target={child.href.startsWith('http') ? "_blank" : "_self"}
															>
																{child.translates.length > 0 ? child.translates[0].title : child.title}
																<span className="absolute inset-0" />
															</a>
														</div>
													</div>
												</div>
											))}
										</div>

									</div>
								</div>
							</div>
						</PopoverContent>
					</Popover>
				</NavbarItem>


			);
		}
		return null;
	}, [lang, topMenu?.navitems]);

	const buildTopNavbar = useCallback(() => {
		const rootItems = topMenu?.navitems.filter(r => r.parentid === null);
		// <className="hidden lg:flex gap-4 justify-start ml-2"
		return (
			rootItems?.map((item: NavItemWithTranslates) => (buildTopTreeNodeNavBar(item as NavItemWithTranslates, 0)))
		);
	}, [buildTopTreeNodeNavBar, topMenu?.navitems]);

	{/* {dictionary[item.title] ? dictionary[item.title] : item.title} */ }

	useEffect(() => {
		buildTopNavbar();
		buildLeftNavbar();
	}, [buildTopNavbar, buildLeftNavbar]);

	function clickDarButton(): void {
		window.open(siteConfig.links.dar, "_blank");
	}

	return (
		<div className='app-navbar'>
			<NextUINavbar className='z-0 py-2' maxWidth="full" position="sticky" height="auto" isMenuOpen={isOpen} onMenuOpenChange={setIsOpen}>
				<NavbarContent className="gap-2 sm:gap-2">
					
					{session?.user && session.user.role === 'ADMINISTRATOR' &&
						<NavbarItem className="flex">
							<Popover placement="bottom" showArrow={false} className='mt-1'>
								<PopoverTrigger>
									<Button
										variant='light'
										disableRipple
										className='min-w-8 h-8 sm:min-w-10 sm:h-10 p-1'
										isIconOnly										
									>									
										<span className="material-symbols-outlined text-default-500">menu</span>
									</Button>
								</PopoverTrigger>
								<PopoverContent className="min-w-[200px] sm:min-w-[220px] max-h-[100vh] overflow-y-auto p-4 ">
									
										{leftMenu && leftMenu.navitems && leftMenu.navitems.length > 0 ? (
											<ul className="space-y-1">
												{leftMenu.navitems
													.filter(item => item.parentid === null && !item.hide)
													.map((item: NavItemWithTranslates) => buildTreeNodeNavBar(item as NavItemWithTranslates, 0))}
											</ul>
										) : (
											<p className="text-sm text-gray-500">{dictionary?.navbar?.noAdminMenuItems || 'No admin menu items'}</p>
										)}
									
								</PopoverContent>
							</Popover>							
						</NavbarItem>
					}
					<NavbarBrand className="flex-grow-0">
						<NextLink className='flex items-center gap-1 w-16 sm:w-20' href={`/${lang}`}>
							<img src={flagEU.src} alt="EU Flag" className="inline-block w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
							<img src={flagUA.src} alt="Ukraine Flag" className="inline-block w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />						
						</NextLink>
					</NavbarBrand>

					{topMenu && (
						<div className="hidden md:flex gap-1 lg:gap-2 overflow-x-auto">
							{buildTopNavbar()}
						</div>
					)}
				</NavbarContent>

				<NavbarContent
					className="flex col sm:hidden" justify="center"
				>		
					<NextLink
						href={`/${lang}`}
						className='uppercase font-semibold text-gray-900 dark:text-gray-400 leading-tight sm:leading-tight md:leading-tight'
					>
						{dictionary?.["Paying agency"] || 'Paying Agency'}
					</NextLink>
				</NavbarContent>

				<NavbarContent
					className="hidden sm:flex basis-1/5 sm:basis-full gap-2" justify="end"
				>
					<NavbarItem className="hidden xl:flex">{searchInput}</NavbarItem>
						{session?.user && (<NavbarItem>
							<UserAvatar src={session.user.image} />
						</NavbarItem>
					)}

					<NavbarItem className="hidden sm:flex gap-2">
						<Button
							isIconOnly
							onPress={clickDarButton}
							aria-label="Open DAR"
							title="Open DAR"
						>
							<DarIcon />
						</Button>
						<LocaleSwitcher />						
					</NavbarItem>
				</NavbarContent>

				<NavbarContent className="sm:hidden basis-1 gap-1" justify="end">
					{/* {session?.user && (
						<NavbarItem>
							<UserAvatar src={session.user.image} />
						</NavbarItem>
					)} */}
					<NavbarItem>

						{/* <Button isIconOnly size='sm' onPress={clickDarButton} >
							<DarIcon />
						</Button> */}

						<Button
							variant="light"
							disableRipple
							className="min-w-8 h-8 p-1"
							isIconOnly
							onPress={() => setIsOpen(!isOpen)}
							aria-label={isOpen ? 'Close menu' : 'Open menu'}
							title={isOpen ? 'Close menu' : 'Open menu'}
						>
							<span className="material-symbols-outlined text-default-500">
								{isOpen ? 'close' : 'menu'}
							</span>
						</Button>
					</NavbarItem>
				</NavbarContent>

				<NavbarMenu className='z-10 pt-4 top-10'>
					<div className="flex flex-col gap-4">
						<div className="mt-4">
							<div className="grid grid-cols-4 gap-2 items-center">
								<div className="col-span-3">
									{searchInput}
								</div>
								<div className="col-span-1 flex justify-end">
									<LocaleSwitcher />
								</div>
							</div>
						</div>
						
						{/* <Button isIconOnly size='sm' onPress={clickDarButton} >
							<DarIcon />
						</Button> */}

						{/* Top menu items for mobile */}
						{topMenu && topMenu.navitems && topMenu.navitems.length > 0 && (
							<div className="flex flex-col gap-2" >
								
								<div  className="flex flex-col gap-1">
													<button
														type="button"
														className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300 py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
														onClick={clickDarButton}
													>
														<span>Go to DAR</span>
														<DarIcon className='w-6 h-6' />
													</button>
								</div>

								{topMenu.navitems
									.filter(item => item.parentid === null && !item.hide)
									.map((item: NavItemWithTranslates) => {
										const children = topMenu.navitems.filter(child => child.parentid === item.id && !child.hide);										
										if (children.length > 0) {
											return (
												<div key={item.id} className="flex flex-col gap-1">
													<button
														type="button"
														className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300 py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
														onClick={() => handleMobileMenuToggle(item.id)}
													>
														<span>{item.translates.length > 0 ? item.translates[0].title : item.title}</span>
														<svg 
															aria-hidden="true" 
															className={`w-4 h-4 transition-transform ${isMobileMenuOpened(item.id) ? "rotate-90" : ""}`} 
															fill="currentColor" 
															viewBox="0 0 20 20" 
															xmlns="http://www.w3.org/2000/svg"
														>
															<path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
														</svg>
													</button>
													{isMobileMenuOpened(item.id) && (
														<div className="flex flex-col gap-1 ml-4">
															{children.map((child: NavItemWithTranslates) => (
																<Link
																	key={child.id}
																	href={child.href.startsWith('http') ? child.href : `/${lang}${child.href}`}
																	color="foreground"
																	className="text-sm hover:text-primary py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
																	target={child.href.startsWith('http') ? "_blank" : "_self"}
																	onPress={() => setIsOpen(false)}
																>
																	{child.translates.length > 0 ? child.translates[0].title : child.title}
																</Link>
															))}
														</div>
													)}
												</div>
											);
										} else {
											return (
												<Link
													key={item.id}
													href={item.href.startsWith('http') ? item.href : `/${lang}${item.href}`}
													color="foreground"
													className="text-sm hover:text-primary py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
													target={item.href.startsWith('http') ? "_blank" : "_self"}
													onPress={() => setIsOpen(false)}
												>
													{item.translates.length > 0 ? item.translates[0].title : item.title}
												</Link>
											);
										}
									})}
							</div>
						)}

						{/* Additional controls for mobile */}
						<div className="flex flex-col gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
							{/* Theme Switch */}
							<div className="flex items-center justify-between py-2 px-2">
								<span className="text-sm font-medium text-gray-700 dark:text-gray-300">{dictionary?.navbar?.theme || 'Theme'}</span>
								<ThemeSwitch />
							</div>
							
							{/* Authentication buttons */}
							{session?.user ? (
								<div className="flex items-center justify-between py-2 px-2">
									<SignOutButton redirectTo={`/${lang}`} variant='light' isIconOnly={true}>
										<div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary">
											<span className="material-symbols-outlined text-lg">logout</span>							
										</div>
									</SignOutButton>
									<span className="text-xs text-gray-500">{session.user.email}</span>
								</div>
							) : (
								<div className="flex flex-col gap-2">
									<Link
										href={`/${lang}/auth/signin`}
										color="foreground"
										className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
										onPress={() => setIsOpen(false)}
									>
										<span className="material-symbols-outlined text-lg">login</span>
										<span>{dictionary?.navbar?.signIn || 'Sign In'}</span>
									</Link>
									<Link
										href={`/${lang}/auth/register`}
										color="foreground"
										className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
										onPress={() => setIsOpen(false)}
									>
										<span className="material-symbols-outlined text-lg">person_add</span>
										<span>{dictionary?.navbar?.register || 'Register'}</span>
									</Link>
								</div>
							)}
						</div>

						{/* User profile section for mobile - moved below menu */}
						{/* {session?.user && (
							<div className="flex items-center gap-3 py-2 border-b border-gray-200 dark:border-gray-700">
								<UserAvatar src={session.user.image} />
								<div className="flex flex-col">
									<span className="text-sm font-medium">{session.user.name || session.user.email}</span>
									<span className="text-xs text-gray-500">{session.user.email}</span>
								</div>
							</div>
						)} */}

						<Link
							href={`/${lang}/faq`}
							color="foreground"
							className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
							title={dictionary?.navbar?.helpCenterTitle || 'Help Center & User Guide'}
							onPress={() => setIsOpen(false)}
						>
							<span className="material-symbols-outlined text-lg">help_outline</span>
							<span>{dictionary?.navbar?.helpCenter || 'Help Center'}</span>
						</Link>
						
						
					</div>
					
					
				</NavbarMenu>
			</NextUINavbar>
		</div>
	);
};
